import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './CodeBattle.css';

const CodeBattle = ({ isOpen, onClose, onJoinBattle }) => {
  const [activeTab, setActiveTab] = useState('browse'); // browse, create, mybattles
  const [availableBattles, setAvailableBattles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [battleCode, setBattleCode] = useState('');
  
  // Create battle form
  const [createForm, setCreateForm] = useState({
    questionId: '',
    maxParticipants: 10,
    timeLimitMinutes: 30,
    isPublic: true
  });

  useEffect(() => {
    if (isOpen && activeTab === 'browse') {
      fetchAvailableBattles();
    }
  }, [isOpen, activeTab]);

  const fetchAvailableBattles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/battles/available');
      setAvailableBattles(response.data);
    } catch (error) {
      console.error('Error fetching battles:', error);
    } finally {
      setLoading(false);
    }
  };

  const joinBattle = async (code) => {
    try {
      await api.post('/api/battles/join', { battleCode: code });
      alert(`Successfully joined battle: ${code}`);
      if (onJoinBattle) {
        onJoinBattle(code);
      }
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to join battle');
    }
  };

  const createBattle = async () => {
    try {
      const response = await api.post('/api/battles/create', createForm);
      alert(`Battle created! Code: ${response.data.battleCode}`);
      setBattleCode(response.data.battleCode);
      setActiveTab('browse');
      fetchAvailableBattles();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create battle');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'EASY': return '#4caf50';
      case 'MEDIUM': return '#ff9800';
      case 'HARD': return '#f44336';
      case 'EXPERT': return '#9c27b0';
      default: return '#888';
    }
  };

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'EASY': return '⭐';
      case 'MEDIUM': return '⭐⭐';
      case 'HARD': return '⭐⭐⭐';
      case 'EXPERT': return '💎';
      default: return '❓';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="code-battle-modal-overlay" onClick={onClose}>
      <div className="code-battle-modal" onClick={(e) => e.stopPropagation()}>
        <div className="battle-header">
          <h2>⚔️ Code Battle Royale</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="battle-tabs">
          <button 
            className={`tab ${activeTab === 'browse' ? 'active' : ''}`}
            onClick={() => setActiveTab('browse')}
          >
            🎮 Browse Battles
          </button>
          <button 
            className={`tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            ➕ Create Battle
          </button>
          <button 
            className={`tab ${activeTab === 'join' ? 'active' : ''}`}
            onClick={() => setActiveTab('join')}
          >
            🔑 Join by Code
          </button>
        </div>

        <div className="battle-content">
          {/* Browse Battles Tab */}
          {activeTab === 'browse' && (
            <div className="browse-section">
              <div className="section-header">
                <h3>Available Battles</h3>
                <button className="refresh-btn" onClick={fetchAvailableBattles}>
                  🔄 Refresh
                </button>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading battles...</p>
                </div>
              ) : availableBattles.length > 0 ? (
                <div className="battles-grid">
                  {availableBattles.map((battle) => (
                    <div key={battle.id} className="battle-card">
                      <div className="battle-card-header">
                        <div className="battle-title">{battle.questionTitle}</div>
                        <div 
                          className="battle-difficulty"
                          style={{ color: getDifficultyColor(battle.questionDifficulty) }}
                        >
                          {getDifficultyIcon(battle.questionDifficulty)} {battle.questionDifficulty}
                        </div>
                      </div>

                      <div className="battle-info">
                        <div className="info-row">
                          <span className="info-icon">👥</span>
                          <span>{battle.currentParticipants}/{battle.maxParticipants} Players</span>
                        </div>
                        <div className="info-row">
                          <span className="info-icon">⏱️</span>
                          <span>{battle.timeLimitMinutes} minutes</span>
                        </div>
                        <div className="info-row">
                          <span className="info-icon">🔑</span>
                          <span className="battle-code-display">{battle.battleCode}</span>
                        </div>
                      </div>

                      <button 
                        className="join-battle-btn"
                        onClick={() => joinBattle(battle.battleCode)}
                        disabled={battle.currentParticipants >= battle.maxParticipants}
                      >
                        {battle.currentParticipants >= battle.maxParticipants ? '🔒 Full' : '⚔️ Join Battle'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">🎯</div>
                  <h3>No Active Battles</h3>
                  <p>Be the first to create a battle!</p>
                  <button 
                    className="create-first-btn"
                    onClick={() => setActiveTab('create')}
                  >
                    Create New Battle
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Create Battle Tab */}
          {activeTab === 'create' && (
            <div className="create-section">
              <h3>⚔️ Create New Battle</h3>
              <div className="create-form">
                <div className="form-group">
                  <label>Question ID</label>
                  <input
                    type="number"
                    value={createForm.questionId}
                    onChange={(e) => setCreateForm({...createForm, questionId: e.target.value})}
                    placeholder="Enter question ID"
                  />
                  <small>Choose a question for the battle</small>
                </div>

                <div className="form-group">
                  <label>Max Participants</label>
                  <input
                    type="number"
                    min="2"
                    max="100"
                    value={createForm.maxParticipants}
                    onChange={(e) => setCreateForm({...createForm, maxParticipants: parseInt(e.target.value)})}
                  />
                  <small>2-100 players</small>
                </div>

                <div className="form-group">
                  <label>Time Limit (minutes)</label>
                  <input
                    type="number"
                    min="5"
                    max="120"
                    value={createForm.timeLimitMinutes}
                    onChange={(e) => setCreateForm({...createForm, timeLimitMinutes: parseInt(e.target.value)})}
                  />
                  <small>5-120 minutes</small>
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={createForm.isPublic}
                      onChange={(e) => setCreateForm({...createForm, isPublic: e.target.checked})}
                    />
                    Public Battle (visible to all)
                  </label>
                </div>

                <button className="create-battle-btn" onClick={createBattle}>
                  🎮 Create Battle
                </button>

                {battleCode && (
                  <div className="battle-created">
                    <div className="success-icon">✅</div>
                    <p>Battle Created Successfully!</p>
                    <div className="created-code">
                      Code: <strong>{battleCode}</strong>
                    </div>
                    <p className="share-text">Share this code with others to join</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Join by Code Tab */}
          {activeTab === 'join' && (
            <div className="join-section">
              <div className="join-container">
                <div className="join-icon">🔑</div>
                <h3>Join Battle by Code</h3>
                <p>Enter the 6-character battle code to join</p>
                
                <input
                  type="text"
                  className="code-input"
                  maxLength="6"
                  placeholder="ABCD12"
                  value={battleCode}
                  onChange={(e) => setBattleCode(e.target.value.toUpperCase())}
                />

                <button 
                  className="join-code-btn"
                  onClick={() => joinBattle(battleCode)}
                  disabled={battleCode.length !== 6}
                >
                  Join Battle
                </button>

                <div className="join-info">
                  <h4>How It Works:</h4>
                  <ol>
                    <li>Get a battle code from a friend or create your own</li>
                    <li>Enter the code above and click Join</li>
                    <li>Wait for the battle to start</li>
                    <li>Compete to solve the problem fastest with best quality</li>
                    <li>Climb the leaderboard and earn rewards!</li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeBattle;


